import React, { useState, useEffect } from "react";
import { ref, push, set, get, update } from "firebase/database";
import { db } from "../firebase";
import useFile from "../hooks/useFile";
import Confirm from "./Confirm";
import Popup from "./Popup";

const AddTeam = ({ gameId, selectedTeam = null, onClose }) => {
  const isEditMode = selectedTeam !== null;
  const [team, setTeam] = useState({
    teamName: "",
    bidderUid: "",
    purseLeft: 0,
    logo: "",
    fileId: null,
  });
  const { uploadImage, uploading, clearError, error: uploadError, deleteFile } = useFile();
  const [preview, setPreview] = useState(null);
  const [bidders, setBidders] = useState({});
  const [auctionSettings, setAuctionSettings] = useState(null);
  const [currentTeamCount, setCurrentTeamCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState({ isOpen: false, message: '', title: '', type: 'warning', onConfirm: null });
  const [popup, setPopup] = useState({ isOpen: false, message: '', title: '', type: 'info' });

  // Load selected team data when in edit mode
  useEffect(() => {
    if (isEditMode && selectedTeam) {
      setTeam({
        teamName: selectedTeam.teamName || "",
        bidderUid: selectedTeam.bidderUid || "",
        purseLeft: selectedTeam.purseLeft || 0,
        logo: selectedTeam.logo || "",
        fileId: selectedTeam.fileId || null,
      });
      if (selectedTeam.logo) {
        setPreview(selectedTeam.logo);
      }
    }
  }, [isEditMode, selectedTeam]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch auction settings
        const settingsRef = ref(db, `games/${gameId}/auctionSettings`);
        const settingsSnapshot = await get(settingsRef);
        if (settingsSnapshot.exists()) {
          const settings = settingsSnapshot.val();
          setAuctionSettings(settings);
          // Auto-set purseLeft from purseValue
          setTeam((prev) => ({
            ...prev,
            purseLeft: settings.purseValue || 0
          }));
        }

        // Fetch bidders for this game
        const usersRef = ref(db, 'users');
        const usersSnapshot = await get(usersRef);
        if (usersSnapshot.exists()) {
          const users = usersSnapshot.val();
          const gameBidders = {};
          Object.keys(users).forEach((uid) => {
            // Filter bidders by role and gameId
            if (users[uid].role === 'bidder' && users[uid].gameId === gameId) {
              gameBidders[uid] = users[uid];
            }
          });
          setBidders(gameBidders);
        } else {
          setBidders({});
        }

        // Count current teams
        const teamsRef = ref(db, `games/${gameId}/teams`);
        const teamsSnapshot = await get(teamsRef);
        if (teamsSnapshot.exists()) {
          const teams = teamsSnapshot.val();
          setCurrentTeamCount(Object.keys(teams).length);
        } else {
          setCurrentTeamCount(0);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [gameId]);

  const handleChange = (f, v) => {
    setTeam({ ...team, [f]: v });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      setPreview(null);
      setTeam({ ...team, logo: "", fileId: null });
      return;
    }

    // If there's an existing uploaded file, delete it first
    if (team.fileId) {
      await deleteFile(team.fileId);
    }

    // Create preview first
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);

    // Upload immediately when file is selected
    clearError();
    const result = await uploadImage(selectedFile, {
      maxSize: 500 * 1024,
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    });

    if (result.success) {
      // Store the uploaded file info in team state
      setTeam({
        ...team,
        logo: result.fileUrl,
        fileId: result.fileId,
      });
    } else {
      // Show error and clear preview
      setPopup({
        isOpen: true,
        title: 'Upload Failed',
        message: result.error || "Failed to upload logo",
        type: "error",
      });
      setPreview(null);
      e.target.value = "";
    }
  };

  const proceedWithTeamCreation = async (purseValue) => {
    setCurrentTeamCount(currentTeamCount + 1);
    try {
      const teamRef = push(ref(db, `games/${gameId}/teams`));

      await set(teamRef, {
        teamName: team.teamName,
        bidderUid: team.bidderUid,
        purseLeft: purseValue,
        logo: team.logo || "",
        fileId: team.fileId || null,
        players: {},
        createdAt: new Date().toISOString(),
      });

      // Reset form with purseValue from settings
      setTeam({ 
        teamName: "", 
        bidderUid: "", 
        purseLeft: auctionSettings.purseValue || 0,
        logo: "",
        fileId: null,
      });
      setPreview(null);
    } catch (error) {
      console.error("Error adding team:", error);
      setPopup({
        isOpen: true,
        title: 'Team Creation Failed',
        message: "Failed to add team: " + error.message,
        type: 'error'
      });
    }
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    if (!gameId) {
      setPopup({
        isOpen: true,
        title: 'Missing Game ID',
        message: "Game ID missing",
        type: 'error'
      });
      return;
    }

    // Check if image is still uploading
    if (uploading) {
      setPopup({
        isOpen: true,
        title: 'Upload in Progress',
        message: "Please wait for logo upload to complete",
        type: "info",
      });
      return;
    }

    // Check if auction settings exist
    if (!auctionSettings) {
      setPopup({
        isOpen: true,
        title: 'Configuration Required',
        message: "Please configure auction settings first!",
        type: 'warning'
      });
      return;
    }

    // Check if total teams limit is reached (only for add mode)
    if (!isEditMode && auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams) {
      setPopup({
        isOpen: true,
        title: 'Team Limit Reached',
        message: `Cannot add more teams! Maximum limit of ${auctionSettings.totalTeams} teams reached. Current teams: ${currentTeamCount}`,
        type: 'warning'
      });
      return;
    }

    // Validate purse value
    const purseValue = Number(team.purseLeft);
    if (!purseValue || purseValue <= 0) {
      setPopup({
        isOpen: true,
        title: 'Invalid Purse Amount',
        message: "Please enter a valid purse amount!",
        type: 'warning'
      });
      return;
    }

    if (isEditMode) {
      // Update existing team
      try {
        const { id } = selectedTeam;
        await update(ref(db, `games/${gameId}/teams/${id}`), {
          teamName: team.teamName,
          bidderUid: team.bidderUid,
          purseLeft: purseValue,
          logo: team.logo || "",
          fileId: team.fileId || null,
        });

        setPopup({
          isOpen: true,
          title: 'Team Updated',
          message: "Team updated successfully!",
          type: 'success'
        });

        // Close edit mode
        if (onClose) {
          setTimeout(() => onClose(), 1500);
        }
      } catch (error) {
        console.error("Error updating team:", error);
        setPopup({
          isOpen: true,
          title: 'Update Failed',
          message: "Failed to update team: " + error.message,
          type: 'error'
        });
      }
    } else {
      // Check if purse value matches settings (optional warning for new teams)
      if (Number(auctionSettings.purseValue) && purseValue !== Number(auctionSettings.purseValue)) {
        setConfirm({
          isOpen: true,
          title: 'Purse Value Mismatch',
          message: `The purse value (৳${purseValue}) differs from the configured purse value (৳${auctionSettings.purseValue}). Do you want to proceed?`,
          type: 'warning',
          onConfirm: async () => {
            await proceedWithTeamCreation(purseValue);
            setConfirm({ isOpen: false, message: '', title: '', onConfirm: null });
          }
        });
        return;
      }

      await proceedWithTeamCreation(purseValue);
    }
  };

  if (loading) {
    return (
      <div className="card shadow-lg border-0 p-4" style={{ borderRadius: "15px" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!auctionSettings) {
    return (
      <div className="card shadow-lg border-0 p-4" style={{ borderRadius: "15px" }}>
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Auction settings not configured!</strong>
          <p className="mb-0 mt-2">
            Please configure auction settings (Purse Value and Total Teams) before adding teams.
          </p>
        </div>
      </div>
    );
  }

  const teamsRemaining = auctionSettings.totalTeams 
    ? auctionSettings.totalTeams - currentTeamCount 
    : "Unlimited";

  return (
    <div className="card shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
      <div className="card-header bg-gradient text-white py-3" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0 fw-bold">
            <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-shield-plus'} me-2`}></i>
            {isEditMode ? 'Edit Team' : 'Add Team'}
          </h4>
          {isEditMode && onClose && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={onClose}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-4">
        {/* Info Banner */}
        <div className="alert alert-info mb-4" style={{ borderRadius: "10px" }}>
          <div className="row g-3">
            <div className="col-md-4">
              <strong>
                <i className="bi bi-wallet2 me-2"></i>
                Purse Value:
              </strong>
              <div className="fs-5 fw-bold text-primary">৳{auctionSettings.purseValue?.toLocaleString() || 0}</div>
            </div>
            <div className="col-md-4">
              <strong>
                <i className="bi bi-shield me-2"></i>
                Total Teams:
              </strong>
              <div className="fs-5 fw-bold text-primary">{auctionSettings.totalTeams || "Not Set"}</div>
            </div>
            <div className="col-md-4">
              <strong>
                <i className="bi bi-bar-chart me-2"></i>
                Remaining:
              </strong>
              <div className="fs-5 fw-bold text-success">{teamsRemaining}</div>
            </div>
          </div>
        </div>

        {/* Warning if limit reached */}
        {auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams && (
          <div className="alert alert-danger" style={{ borderRadius: "10px" }}>
            <i className="bi bi-exclamation-octagon me-2"></i>
            <strong>Maximum team limit reached!</strong> You have {currentTeamCount} out of {auctionSettings.totalTeams} teams.
          </div>
        )}

        <form onSubmit={handleAddTeam}>
          {/* Team Logo Upload */}
          <div className="mb-4 text-center">
            <div className="player-photo-upload-small mb-3">
              {preview ? (
                <div className="photo-preview-small">
                  <img
                    src={preview}
                    alt="Team logo preview"
                    className="rounded-circle"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger remove-photo-small"
                    onClick={async () => {
                      if (team.fileId) {
                        try {
                          setDeleting(true);
                          await deleteFile(team.fileId);
                        } catch (e) {
                        } finally {
                          setDeleting(false);
                        }
                      }
                      setPreview(null);
                      setTeam({ ...team, logo: "", fileId: null });
                      const input = document.getElementById("logoUpload");
                      if (input) input.value = "";
                    }}
                    disabled={uploading || deleting || (!isEditMode && auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams)}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                </div>
              ) : (
                <div className="photo-placeholder-small">
                  {uploading || deleting ? (
                    <>
                      <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <p className="mb-0 small">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-fill"></i>
                      <p className="mb-0 small">Logo</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <input
              type="file"
              id="logoUpload"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading || (!isEditMode && auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams)}
              className="d-none"
            />
            <label
              htmlFor="logoUpload"
              className={`btn btn-sm ${uploading || deleting ? "btn-secondary" : "btn-outline-primary"}`}
            >
              {uploading || deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  {preview ? "Change Logo" : "Upload Logo"}
                </>
              )}
            </label>
            <small className="d-block text-muted mt-1">Optional - Max 500KB</small>
            {uploadError && (
              <small className="d-block text-danger mt-1">
                <i className="bi bi-exclamation-circle me-1"></i>
                Please try again after some time.
              </small>
            )}
          </div>

          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">
                <i className="bi bi-shield-fill-check me-2 text-primary"></i>
                Team Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter team name"
                required
                value={team.teamName}
                onChange={(e) => handleChange("teamName", e.target.value)}
                disabled={!isEditMode && auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams}
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                <i className="bi bi-person-badge me-2 text-primary"></i>
                Select Bidder <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={team.bidderUid}
                onChange={(e) => handleChange("bidderUid", e.target.value)}
                required
                disabled={!isEditMode && auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams}
              >
                <option value="">Choose a bidder</option>
                {Object.keys(bidders).map((uid) => {
                  const bidder = bidders[uid];
                  return (
                    <option key={uid} value={uid}>
                      {bidder.name} ({bidder.email})
                    </option>
                  );
                })}
              </select>
              {Object.keys(bidders).length === 0 && (
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  No bidders available for this game. Bidders need to sign up and join this game first.
                </small>
              )}
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                <i className="bi bi-cash-coin me-2 text-primary"></i>
                Initial Purse Amount <span className="text-danger">*</span>
                <i 
                  className="bi bi-info-circle ms-2 text-info" 
                  title="Auto-filled from auction settings"
                ></i>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter purse amount"
                required
                value={team.purseLeft}
                onChange={(e) => handleChange("purseLeft", e.target.value)}
                disabled={!isEditMode && auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams}
              />
              <small className="text-muted">
                <i className="bi bi-lightbulb me-1"></i>
                Recommended: ৳{auctionSettings.purseValue?.toLocaleString()}
              </small>
            </div>

            <div className="col-12 mt-4">
              <button 
                type="submit" 
                className="btn btn-success w-100 py-2 fw-semibold"
                disabled={(!isEditMode && auctionSettings.totalTeams && currentTeamCount >= auctionSettings.totalTeams) || uploading}
                style={{ fontSize: "1.05rem" }}
              >
                {uploading || deleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Uploading...
                  </>
                ) : isEditMode ? (
                  <>
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Update Team
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle-fill me-2"></i>
                    Add Team ({currentTeamCount}/{auctionSettings.totalTeams || "∞"})
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <Confirm
        isOpen={confirm.isOpen}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ isOpen: false, message: '', title: '', onConfirm: null })}
        message={confirm.message}
        title={confirm.title}
        type={confirm.type}
      />

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', title: '', type: 'info' })}
        message={popup.message}
        title={popup.title}
        type={popup.type}
      />

      <style jsx>{`
        .player-photo-upload-small {
          position: relative;
          width: 100px;
          height: 100px;
          margin: 0 auto;
        }

        .photo-placeholder-small {
          width: 100px;
          height: 100px;
          border: 2px dashed #dee2e6;
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .photo-placeholder-small:hover {
          border-color: #667eea;
          background: #f0f2ff;
        }

        .photo-placeholder-small i {
          font-size: 2rem;
          color: #6c757d;
          margin-bottom: 5px;
        }

        .photo-placeholder-small p {
          color: #6c757d;
        }

        [data-theme="dark"] .photo-placeholder-small {
          background: #2d2d44;
          border-color: #4a4a6a;
        }

        [data-theme="dark"] .photo-placeholder-small:hover {
          border-color: #667eea;
          background: #3d3d5c;
        }

        [data-theme="dark"] .photo-placeholder-small i {
          color: #a0a0a0;
        }

        [data-theme="dark"] .photo-placeholder-small p {
          color: #a0a0a0;
        }

        .photo-preview-small {
          position: relative;
          width: 100px;
          height: 100px;
        }

        .photo-preview-small img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border: 3px solid #667eea;
          box-shadow: 0 3px 10px rgba(102, 126, 234, 0.3);
        }

        .remove-photo-small {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        .bg-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default AddTeam;
